import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';

import AppScriptAPI from '../../services/appscriptAPI';
import UpdateLayout from '../../layouts/UpdateLayout';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';

function UpdateBasic() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [gnDivisions, setGnDivisions] = useState([]);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    nic: '',
    province: '',
    district: '',
    dsDivision: '',
    gnDivision: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message:
          'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getBasicInfo(recordId, sheetId);

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load record.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setGnDivisions((response.gnDivisions || []).sort((a, b) => a.name.localeCompare(b.name)));

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        nic: response.record.nic || '',
        province: response.record.province || '',
        district: response.record.district || '',
        dsDivision: response.record.dsDivision || '',
        gnDivision: response.record.gnDivision || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const validateNIC = (nic) => {
    if (!nic) return true; // NIC is optional
    const nicPattern = /^([0-9]{9}[VX]|[0-9]{12})$/;
    return nicPattern.test(nic.toUpperCase());
  };

  const validateName = (name) => {
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      return {
        valid: false,
        message: 'Name must be at least 2 characters long.',
      };
    }
    if (trimmedName.length > 200) {
      return {
        valid: false,
        message: 'Name is too long (max 200 characters).',
      };
    }
    return { valid: true };
  };

  const handleSave = async () => {
    // Validate
    const nameValidation = validateName(formData.name);
    if (!nameValidation.valid) {
      setAlert({ show: true, message: nameValidation.message, type: 'danger' });
      return;
    }

    const nicValue = formData.nic.trim().toUpperCase();
    if (nicValue && !validateNIC(nicValue)) {
      setAlert({
        show: true,
        message:
          'Invalid NIC format. Please enter a valid NIC number (9 digits + V/X or 12 digits).',
        type: 'danger',
      });
      return;
    }

    if (!formData.gnDivision) {
      setAlert({
        show: true,
        message: 'Please select a GN Division.',
        type: 'danger',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        uniqueId: recordData.uniqueId,
        name: formData.name.trim(),
        nic: nicValue,
        gnDivision: formData.gnDivision,
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateBasicInfo(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Basic information updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/contact/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate('/search');
  };

  if (loading) {
    return (
      <UpdateLayout currentStep={1} title="Update Basic Information">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading record data...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout currentStep={1} title="Update Basic Information">
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load record data.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={1}
      title="Update Basic Information"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
      showPrevious={false}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Location Information</div>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Province</Form.Label>
              <Form.Control
                type="text"
                value={formData.province}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>District</Form.Label>
              <Form.Control
                type="text"
                value={formData.district}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>DS Division</Form.Label>
              <Form.Control
                type="text"
                value={formData.dsDivision}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>
            GN Division <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            name="gnDivision"
            value={formData.gnDivision}
            onChange={handleInputChange}
            required
            disabled={!canEdit}
          >
            <option value="">Select GN Division</option>
            {gnDivisions.map((gn, index) => (
              <option key={index} value={gn.name}>
                {gn.name}
              </option>
            ))}
          </Form.Select>
          <Form.Text>Select the Grama Niladhari Division.</Form.Text>
        </Form.Group>

        <div className="section-header">Personal Information</div>

        <Form.Group className="mb-3">
          <Form.Label>Unique ID</Form.Label>
          <Form.Control
            type="text"
            value={formData.uniqueId}
            className="readonly-field"
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter full name"
            required
            disabled={!canEdit}
          />
          <Form.Text>
            Enter the person's full name as it appears on their NIC.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>NIC Number</Form.Label>
          <Form.Control
            type="text"
            name="nic"
            value={formData.nic}
            onChange={handleInputChange}
            placeholder="Ex. 199512345678 or 951234567V"
            maxLength="12"
            disabled={!canEdit}
          />
          <Form.Text>Enter 9 digits + V/X or 12 digits (optional).</Form.Text>
        </Form.Group>
      </Form>
    </UpdateLayout>
  );
}

export default UpdateBasic;
